<template>
  <div class="file-preview">
    <div class="file-preview__pdf-page" v-if="ifPdf(attach) && pageTotal > 1">
      <i class="ivu-icon-icon-pg-z file-preview__pdf-page-icon file-preview__pdf-page-icon-up"></i>
      <span>当前为PDF，可滚动上下翻页，{{ pageNo }}/{{ pageTotal }}</span>
    </div>
    <div class="file-preview__pdf-page" v-if="ifPdf(attach) && pageTotal > 1">
      <i
        class="ivu-icon-icon-pg-z file-preview__pdf-page-icon file-preview__pdf-page-icon-down"
      ></i>
      <span>当前为PDF，可滚动上下翻页，{{ pageNo }}/{{ pageTotal }}</span>
    </div>
    <div
      class="file-preview__box"
      v-if="!!attach"
      ref="boxRef"
      :style="{
        top: `${boxStyle.top}px`,
        left: `${boxStyle.left}px`,
        transform: `scale(${zoomNum})`,
      }"
      :class="{ 'file-preview__box--drag': space && isMousedown }"
      @mousedown="onMousedown"
      @mouseup="onMouseup"
      @mousemove="onMousemove"
      @mouseout="onMouseup"
      v-mousewheel="onMousewheel"
    >
      <img
        class="file-preview__box-img"
        draggable="false"
        ref="imgRef"
        v-if="ifImg(attach)"
        :src="attach.url"
      />
      <template v-else-if="ifPdf(attach)">
        <img
          v-if="transformPdfImg && !showPdfInIframe"
          ref="pdfImgRef"
          class="file-preview__box-img"
          draggable="false"
          :src="imgAttach.url"
        />
        <iframe
          v-else-if="showPdfInIframe"
          class="file-preview__box-iframe"
          ref="pdfIframeRef"
          :src="iframeAttach.url"
        ></iframe>
        <canvas v-else ref="canvasRef"></canvas>
      </template>
      <img class="file-preview__box-img" v-else :src="otherAttach" />
    </div>
  </div>
</template>
<script setup>
import { computed, ref, onUnmounted, watch } from 'vue'
import { ifImg, ifPdf } from '../util/file-util'
import { usePdf } from '../hook/use-pdf'
import { useOther } from '../hook/use-other'
import { throttle, debounce } from 'lodash-es'
import { vMousewheel, numberAdd, numberSub } from '../../../utils/index'

const props = defineProps({
  value: [Object, String],
  //为 false时注销事件
  space: {
    type: Boolean,
    default: false,
  },
  visible: {
    type: Boolean,
    default: true,
  },
  pdfReplaceUrl: String,
  transformPdfImg: {
    type: Boolean,
    default: true,
  },
  // showPdfInIframe transformPdfImg 同时为true时showPdfInIframe优先级更高
  showPdfInIframe: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['update:space'])

const boxRef = ref(null)
const attach = computed(() =>
  typeof props.value === 'string' ? { url: props.value } : props.value
)
const { canvasRef, nextPage, lastPage, pageNo, pageTotal, imgAttach, iframeAttach } = usePdf({
  attach,
  boxRatio: () => boxRef.value.clientWidth / boxRef.value.clientHeight,
  pdfReplaceUrl: props.pdfReplaceUrl,
  transformPdfImg: props.transformPdfImg,
  showPdfInIframe: props.showPdfInIframe,
})
const { otherAttach } = useOther({ attach })

const boxStyle = ref({
  top: 0,
  left: 0,
})
const isMousedown = ref(false)
const mousedownOffset = {
  clientX: 0,
  clientY: 0,
}
const onMousedown = (event) => {
  isMousedown.value = true
  if (props.space) {
    mousedownOffset.clientX = event.clientX - boxStyle.value.left
    mousedownOffset.clientY = event.clientY - boxStyle.value.top
  }
}
const onMouseup = () => {
  isMousedown.value = false
}
const onMousemove = throttle((event) => {
  if (props.space && isMousedown.value) {
    boxStyle.value.top = event.clientY - mousedownOffset.clientY
    boxStyle.value.left = event.clientX - mousedownOffset.clientX
  }
}, 10)
const onKeydown = debounce(
  (event) => {
    if (event.keyCode === 32 && !props.space) {
      emit('update:space', true)
    }
  },
  100,
  { leading: true, trailing: false }
)
const onKeyup = debounce(
  (event) => {
    if (event.keyCode === 32 && props.space) {
      emit('update:space', false)
    }
  },
  100,
  { leading: true, trailing: false }
)

const zoomNum = ref(1)
//deltaY > 0 时缩小
const zoom = (deltaY, step = 0.05) => {
  if (deltaY > 0 && zoomNum.value > step) {
    zoomNum.value = numberSub(zoomNum.value, step)
  } else if (deltaY < 0 && zoomNum.value < 3.6) {
    zoomNum.value = numberAdd(zoomNum.value, step)
  }
}
const swichPage = debounce((deltaY) => {
  deltaY > 0 ? nextPage() : lastPage()
}, 100)
const onMousewheel = debounce((event) => {
  const ctrlKey = event.ctrlKey
  if (!ctrlKey) {
    swichPage(event.deltaY)
  } else {
    zoom(event.deltaY)
  }
}, 20)
//禁止浏览器放大缩小
const __listenHandler = function (event) {
  if (event.ctrlKey === true || event.metaKey === true) {
    event.preventDefault()
  }
}
watch(
  () => props.visible,
  () => {
    if (props.visible) {
      document.addEventListener('keydown', onKeydown)
      document.addEventListener('keyup', onKeyup)

      window.addEventListener('mousewheel', __listenHandler, { passive: false })
      // firefox
      window.addEventListener('DOMMouseScroll', __listenHandler, { passive: false })
    } else {
      document.removeEventListener('keydown', onKeydown)
      document.removeEventListener('keyup', onKeyup)
      window.removeEventListener('mousewheel', __listenHandler)
      window.removeEventListener('DOMMouseScroll', __listenHandler)
    }
  },
  { immediate: true }
)
onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown)
  document.removeEventListener('keyup', onKeyup)
  window.removeEventListener('mousewheel', __listenHandler)
  window.removeEventListener('DOMMouseScroll', __listenHandler)
})

const imgRef = ref(null)
const pdfImgRef = ref(null)
defineExpose({
  nextPage,
  lastPage,
  zoom,
  initSize() {
    if (ifImg(attach.value)) {
      const naturalWidth = imgRef.value.naturalWidth
      const imgWidth = imgRef.value.width
      const scal = naturalWidth / imgWidth
      zoomNum.value = scal
    } else if (!props.transformPdfImg && !props.showPdfInIframe && ifPdf(attach.value)) {
      const width = canvasRef.value.width
      const clientWidth = canvasRef.value.clientWidth
      const scal = width / clientWidth
      zoomNum.value = scal
    } else if (props.transformPdfImg && !props.showPdfInIframe && ifPdf(attach.value)) {
      const naturalWidth = pdfImgRef.value.naturalWidth
      const imgWidth = pdfImgRef.value.width
      const scal = naturalWidth / imgWidth
      zoomNum.value = scal
    } else if (props.showPdfInIframe && ifPdf(attach.value)) {
      zoomNum.value = 1
    }
  },
  setZoomNum(val) {
    zoomNum.value = val
  },
  getZoomNum() {
    return zoomNum.value
  },
  download() {
    const link = document.createElement('a')
    link.style.display = 'none'
    link.download = attach.value?.name
    link.href = attach.value.url
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  },
})
</script>
<style lang="scss">
@import '../style/file-preview.scss';
</style>
